import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

const VerificationModal = ({ isOpen, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFrontChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 10MB',
          variant: 'destructive',
        });
        return;
      }
      setFrontFile(file);
    }
  };

  const handleBackChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 10MB',
          variant: 'destructive',
        });
        return;
      }
      setBackFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!frontFile || !backFile) {
      toast({
        title: 'Missing documents',
        description: 'Please upload both front and back of your ID document',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('documentFront', frontFile);
      formData.append('documentBack', backFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/auth/verify`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        toast({
          title: 'Documents Submitted',
          description: response.data.message,
        });
        setFrontFile(null);
        setBackFile(null);
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Failed to submit documents. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-[#0d6571]" />
            Identity Verification
          </DialogTitle>
          <DialogDescription>
            Upload your passport or ID card to verify your account
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Info Alert */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-semibold mb-1">
                  Required Documents
                </p>
                <p className="text-sm text-blue-700">
                  Please upload clear photos or scans of both the front and back of your passport or ID card.
                  Make sure all information is clearly visible.
                </p>
              </div>
            </div>
          </div>

          {/* Front Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Document Front <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#0d6571] transition-colors">
              <input
                type="file"
                id="frontFile"
                accept="image/*,.pdf"
                onChange={handleFrontChange}
                className="hidden"
              />
              <label htmlFor="frontFile" className="cursor-pointer">
                {frontFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-[#0d6571]" />
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">{frontFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(frontFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setFrontFile(null);
                      }}
                      variant="outline"
                      className="ml-4"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-semibold">Click to upload front of document</p>
                    <p className="text-sm text-gray-500 mt-1">PNG, JPG or PDF (max 10MB)</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Back Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Document Back <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#0d6571] transition-colors">
              <input
                type="file"
                id="backFile"
                accept="image/*,.pdf"
                onChange={handleBackChange}
                className="hidden"
              />
              <label htmlFor="backFile" className="cursor-pointer">
                {backFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-[#0d6571]" />
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">{backFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(backFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setBackFile(null);
                      }}
                      variant="outline"
                      className="ml-4"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-semibold">Click to upload back of document</p>
                    <p className="text-sm text-gray-500 mt-1">PNG, JPG or PDF (max 10MB)</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <p className="text-xs text-gray-600">
              🔒 Your documents are transmitted securely and will be used solely for identity verification purposes.
              We comply with all data protection regulations.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !frontFile || !backFile}
              className="flex-1 bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full py-6 text-lg font-semibold"
            >
              {isSubmitting ? 'Sending...' : 'Send for Verification'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={isSubmitting}
              className="flex-1 border-2 border-gray-300 rounded-full py-6 text-lg font-semibold"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationModal;
